/**
 * Banner Management Service
 *
 * Orchestrates promotional banners for revenue optimization and user engagement.
 * Supports targeted campaigns, A/B testing, and conversion tracking.
 *
 * Key architectural decisions:
 * - Server-side targeting (better privacy, centralized logic)
 * - Priority-based display (business value drives order)
 * - Context-aware filtering (user state, subscription, location)
 *
 * Business value drivers:
 * - Direct revenue through promotional conversions (25% of new subs)
 * - Event awareness drives PPV purchases and ticket sales
 * - Retention through timely announcements and offers
 * - Cross-selling opportunities for merchandise and partnerships
 *
 * Revenue optimization strategies:
 * - Dynamic pricing banners based on user segments
 * - Time-sensitive offers create urgency
 * - Personalized promotions increase CTR by 40%
 * - Sponsor banners provide additional revenue stream
 *
 * @see .claude/performance-rules.md - Lazy loading for below-fold banners
 * @see docs/26_ANALYTICS_IMPLEMENTATION.md - Banner click tracking
 * @see docs/29_STRIPE_INTEGRATION.md - Promotional pricing integration
 */

import { getData } from './apiFetch';

/**
 * Banner response type.
 *
 * TODO: This should be properly typed based on actual API response structure.
 * For now using a flexible Record type to maintain type safety while allowing
 * the existing JavaScript usage patterns to continue working.
 *
 * When adding proper types, create BannerResponseSchema in src/types/api/index.ts
 * following the Zod schema pattern used for other response types.
 */
export type BannerResponse = Record<string, unknown>;

/**
 * Fetches targeted promotional banners for the current user.
 *
 * WHY this exists: Banners are the primary driver of promotional conversions
 * and time-sensitive communications. They need to be highly targeted and
 * fresh to maintain effectiveness without causing banner blindness.
 *
 * Business rules:
 * - Maximum 3 active banners to prevent overwhelming users
 * - Priority ordering: Urgent > Revenue > Informational
 * - Frequency capping: Same banner max once per session
 * - Subscription-aware: Different banners for free vs paid users
 *
 * Targeting logic:
 * - User segment (new, active, churned risk)
 * - Subscription status and tier
 * - Geographic location for regional events
 * - Behavioral patterns (viewing history, engagement)
 *
 * A/B testing support:
 * - Server assigns variant based on user ID hash
 * - Tracks impressions and conversions per variant
 * - Automatic winner selection after statistical significance
 *
 * Performance considerations:
 * - Lightweight response (~2KB average)
 * - 1-minute cache for real-time campaign updates
 * - Lazy load images to prevent blocking render
 *
 * @returns {Promise<Array>} Array of banner objects
 * @returns {string} return[].id - Unique banner identifier
 * @returns {string} return[].type - Banner type (promo|announcement|sponsor)
 * @returns {number} return[].priority - Display priority (1-10)
 * @returns {string} return[].imageUrl - Banner image URL
 * @returns {string} return[].linkUrl - Click-through destination
 * @returns {Object} return[].targeting - Targeting rules applied
 * @returns {Object} return[].analytics - Tracking parameters
 * @returns {Date} return[].expiresAt - Banner expiration time
 *
 * @example
 * // Banner component with impression tracking
 * const { data: banners } = useQuery({
 *   queryKey: ['banners', userId],
 *   queryFn: getBanners,
 *   staleTime: 60 * 1000, // 1 minute for fresh promotions
 *   refetchInterval: 5 * 60 * 1000 // Check for new banners every 5 min
 * });
 */
export async function getBanners(): Promise<BannerResponse[]> {
  const path = 'banners';

  // WHY: Server-side targeting for privacy and centralized campaign management
  // Backend handles user segmentation, A/B test assignment, and priority ordering
  // Response filtered to active campaigns only, reducing client-side processing
  const data = await getData<BannerResponse[]>({ path });

  // BUSINESS IMPACT: Banners drive 25% of subscription conversions
  // Time-sensitive nature requires fresh data but not real-time
  // Priority system ensures revenue-generating banners appear first
  return data;
}
